import { useFormik } from "formik";
import React, { useEffect } from "react";
import DropzoneComponent from "../../../Components/Common/DropzoneComponent";
import {
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
} from "reactstrap";
import * as Yup from "yup";
import MenuImageService from "../../../Services/QRCodeServices/MenuItem/MenuImageService";
import toast from "react-hot-toast";

const ImageForm = ({ isOpen, toggle, selectedItem, onSuccess, fetchData }) => {
  console.log("🚀 ~ ImageForm ~ selectedItem:", selectedItem);

  const formik = useFormik({
    initialValues: {
      images: [],
      imageOrder: selectedItem ? selectedItem.imageOrder || 0 : "",
    },
    validationSchema: Yup.object({
      images: Yup.array()
        .max(1, "Only one image allowed")
        .required("Image is required"),
      imageOrder: Yup.number()
        .required("Image order is required")
        .min(0, "Order must be >= 0")
        .typeError("Image order must be a number"),
    }),
    enableReinitialize: true, // This ensures form reinitializes when selectedItem changes
    onSubmit: async (values) => {
      try {
        const formData = new FormData();

        // For create operation, we need an image
        if (
          !selectedItem?.id &&
          (!values.images || values.images.length === 0)
        ) {
          toast.error("Please select an image to upload");
          return;
        }

        if (selectedItem?.id) {
          // Update case (no array)
          formData.append(
            "name",
            values.images?.[0]?.name || selectedItem.name || ""
          );
          formData.append("imageOrder", values.imageOrder.toString());
          if (values.images && values.images.length > 0) {
            formData.append("image", values.images[0]);
          }
        } else {
          // Create case (use array format)
          if (values.images && values.images.length > 0) {
            const imageFile = values.images[0];
            formData.append("dataList[0].name", imageFile.name);
            formData.append(
              "dataList[0].imageOrder",
              values.imageOrder.toString()
            );
            formData.append("dataList[0].image", imageFile);
          }
        }

        let response;
        if (selectedItem?.id) {
          response = await MenuImageService.update(selectedItem.id, formData);
          toast.success("Image updated successfully");
        } else {
          response = await MenuImageService.create(formData);
          toast.success("Image uploaded successfully");
        }

        // Reset form and close modal
        formik.resetForm();
        fetchData();
        toggle();
        if (onSuccess) onSuccess();
      } catch (error) {
        toast.error(
          error?.response?.data?.message || "Failed to process image"
        );
        console.error("Image upload error:", error);
      }
    },
  });

  // Handle modal close - reset form when modal closes
  const handleToggle = () => {
    formik.resetForm();
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleToggle}>
      <ModalHeader toggle={handleToggle}>
        {selectedItem?.id ? "Update Image" : "Upload Image"}
      </ModalHeader>
      <ModalBody>
        <form onSubmit={formik.handleSubmit}>
          <FormGroup>
            <Label for="imageOrder">
              Image Order <span className="text-danger">*</span>
            </Label>
            <Input
              id="imageOrder"
              type="number"
              name="imageOrder"
              min="0"
              step="1"
              value={formik.values.imageOrder}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={formik.touched.imageOrder && !!formik.errors.imageOrder}
              placeholder="Enter image order (e.g., 1, 2, 3...)"
            />
            {formik.touched.imageOrder && formik.errors.imageOrder && (
              <div className="text-danger small mt-1">
                {formik.errors.imageOrder}
              </div>
            )}
          </FormGroup>

          <DropzoneComponent
            title={selectedItem?.id ? "Update Image" : "Upload Image"}
            setFieldValue={formik.setFieldValue}
            images={formik.values.images}
            errors={formik.errors.images}
            touched={formik.touched.images}
            required={!selectedItem} // Only required for new uploads
            selectedItem={selectedItem} // Pass selectedItem to show current image
          />

          <div className="d-flex justify-content-end mt-3">
            <Button
              color="secondary"
              onClick={handleToggle}
              className="me-2"
              type="button"
            >
              Cancel
            </Button>
            <Button
              color="primary"
              type="submit"
              disabled={formik.isSubmitting}
            >
              {formik.isSubmitting ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  {selectedItem?.id ? "Updating..." : "Saving..."}
                </>
              ) : selectedItem?.id ? (
                "Update"
              ) : (
                "Save"
              )}
            </Button>
          </div>
        </form>
      </ModalBody>
    </Modal>
  );
};

export default ImageForm;
